/**
 * ReadingVox Widget - Entry Point (website / CDN bundle)
 *
 * Exports the ReadingVox class (window.ReadingVox) and auto-initialises from
 * the embed tag:
 *
 *   <script src="https://readingvox.com/widget.js" data-key="YOUR_API_KEY" data-voice="heart" async></script>
 *
 * LMS installs (e.g. Canvas Theme Editor custom JS) insert the same tag
 * dynamically with data-preset="canvas"; see lms.ts for the preset.
 *
 * The Chrome extension imports core/ReadingVox directly, so nothing in this
 * file runs in the extension.
 */

import { ReadingVox } from './core/ReadingVox';
import { setExtrasOrigin, EXTRAS_ARE_LAZY } from './lazy';
import { SHARED_CORE_MODULES } from './shared';
import type { ReadingVoxConfig, ToolId, WidgetMode, ThemeMode, TogglePosition } from './types/config';

// Export for ES modules
export { ReadingVox };
export type { ReadingVoxConfig } from './types/config';
export type { SentenceStreamResponse, WordTimestamp, AudioMapping } from './types/api';

// Default export for UMD/CJS (becomes window.ReadingVox)
export default ReadingVox;

// ---------------------------------------------------------------------------
// Auto-init from <script data-key="...">
// ---------------------------------------------------------------------------

const VALID_MODES: WidgetMode[] = ['page', 'block', 'highlight'];
const VALID_THEMES: ThemeMode[] = ['light', 'dark', 'auto'];
const VALID_TOGGLE_POSITIONS: TogglePosition[] = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];

function findScriptTag(): HTMLScriptElement | null {
  // Also set for classic scripts inserted dynamically (e.g. the Canvas Theme
  // Editor loader that appends <script src=".../widget.js" data-key>)
  const current = document.currentScript as HTMLScriptElement | null;
  if (current && current.src) return current;
  return (
    document.querySelector('script[data-key][src*="widget"]') ||
    document.querySelector('script[src*="readingvox"][data-key]') ||
    null
  ) as HTMLScriptElement | null;
}

function parseBool(value: string | undefined): boolean | undefined {
  if (value === undefined) return undefined;
  const v = value.trim().toLowerCase();
  if (v === '' || v === 'true' || v === '1' || v === 'yes') return true;
  if (v === 'false' || v === '0' || v === 'no') return false;
  return undefined;
}

function parseList(value: string | undefined): string[] | undefined {
  if (!value) return undefined;
  const items = value.split(',').map(s => s.trim()).filter(Boolean);
  return items.length ? items : undefined;
}

/**
 * Build a config from the script tag's data-* attributes.
 * Exported for testing.
 */
export function configFromScript(script: HTMLScriptElement, defaultApi: string): ReadingVoxConfig | null {
  const d = script.dataset;
  const apiKey = d.key || d.apiKey;
  if (!apiKey) return null;

  const config: ReadingVoxConfig = {
    apiKey,
    apiEndpoint: (d.api || defaultApi).replace(/\/+$/, ''),
  };

  if (d.voice) config.voice = d.voice as ReadingVoxConfig['voice'];

  // Platform preset (e.g. data-preset="canvas" / "moodle"); "none" opts out of auto-detection
  const preset = d.preset?.trim().toLowerCase();
  if (preset === 'canvas' || preset === 'moodle' || preset === 'none') config.preset = preset;

  if (d.mode && VALID_MODES.includes(d.mode as WidgetMode)) config.mode = d.mode as WidgetMode;

  if (d.position === 'top' || d.position === 'bottom') config.toolbarPosition = d.position;

  if (d.togglePosition && VALID_TOGGLE_POSITIONS.includes(d.togglePosition as TogglePosition)) {
    config.togglePosition = d.togglePosition as TogglePosition;
  }

  const themeMode = d.theme && VALID_THEMES.includes(d.theme as ThemeMode) ? (d.theme as ThemeMode) : undefined;
  if (d.color) {
    config.theme = { mode: themeMode || 'auto', primaryColor: d.color };
  } else if (themeMode) {
    config.theme = themeMode;
  }

  if (d.speed) {
    const speed = parseFloat(d.speed);
    if (!isNaN(speed)) config.speed = Math.max(0.5, Math.min(2, speed));
  }

  const tools = parseList(d.tools);
  if (tools) config.enabledTools = tools as ToolId[];

  const selectors = parseList(d.selectors);
  if (selectors) config.selectors = selectors;

  const showToggle = parseBool(d.showToggle);
  if (showToggle !== undefined) config.showToggle = showToggle;

  if (d.lang) config.contentLanguage = d.lang;
  if (d.uiLang) config.uiLanguage = d.uiLang;
  if (d.documents !== undefined) config.documentLinks = parseBool(d.documents) === true;

  const pulse = parseBool(d.pulse);
  if (pulse !== undefined) config.pulse = pulse;

  const alwaysShow = parseBool(d.alwaysShow);
  if (alwaysShow !== undefined) config.alwaysShowPlayer = alwaysShow;

  const frames = parseBool(d.frames);
  if (frames !== undefined) config.readFrames = frames;
  const frameOrigins = parseList(d.frameOrigins);
  if (frameOrigins) config.frameOrigins = frameOrigins;

  const debug = parseBool(d.debug);
  if (debug) config.debug = true;

  return config;
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  (window as any).ReadingVox = ReadingVox;
  // Lets the lazily loaded extras script reuse core classes (website build only)
  if (EXTRAS_ARE_LAZY) (window as any).__ReadingVoxCore = SHARED_CORE_MODULES;

  const script = findScriptTag();

  // Default API base = this script's own origin (never relative to the publisher's site)
  let scriptOrigin: string | null = null;
  try {
    if (script?.src) scriptOrigin = new URL(script.src, window.location.href).origin;
  } catch {
    scriptOrigin = null;
  }
  if (scriptOrigin) {
    ReadingVox.defaultApiEndpoint = `${scriptOrigin}/api/v1`;
    // Lazily loaded tools come from the same origin as this script
    setExtrasOrigin(scriptOrigin);
  }

  const autoInitEnabled = script && parseBool(script.dataset.autoInit) !== false;
  const config = script && autoInitEnabled
    ? configFromScript(script, ReadingVox.defaultApiEndpoint)
    : null;

  const w = window as any;
  if (config && !w.__readingvoxAutoInit) {
    w.__readingvoxAutoInit = true;

    let autoInstance: ReadingVox | null = null;
    let manualInstanceSeen = false;
    let creatingAuto = false;

    // A manually created instance always wins over the auto one
    ReadingVox.onInstanceCreated((instance) => {
      if (creatingAuto || instance === autoInstance) return;
      manualInstanceSeen = true;
      if (autoInstance) {
        try { autoInstance.destroy(); } catch { /* ignore */ }
        autoInstance = null;
      }
    });

    const start = () => {
      // Let inline DOMContentLoaded handlers that call `new ReadingVox()` run first
      setTimeout(() => {
        if (manualInstanceSeen || ReadingVox.instances.size > 0) return;
        try {
          creatingAuto = true;
          try {
            autoInstance = new ReadingVox(config);
          } finally {
            creatingAuto = false;
          }
          w.readingvox = autoInstance;
          autoInstance.initialize().catch((error) => {
            if (config.debug) console.warn('[ReadingVox] Auto-init failed:', error);
          });
        } catch (error) {
          if (config.debug) console.warn('[ReadingVox] Auto-init failed:', error);
        }
      }, 0);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
      start();
    }
  }
}
